import { Component, DestroyRef, OnInit } from '@angular/core';
import { API, TemplateGroupDTO, TemplateVariableDTO, TemplateVariableType } from '../../../../services/api/api';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ErrorService } from '../../../../services/error.service';
import { ModalService } from '../../../../services/gui/modal/modal.service';
import { UtilService } from '../../../../services/util.service';
import { NavigationService } from '@ngx-seatback/utils';
import { RoutingID } from '../../../../routes/sb-routing-enum';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';

@Component({
  selector: 'sb-page-templateGroup-edit',
  templateUrl: './template-group-edit.component.html',
  styleUrls: ['./template-group-edit.component.scss'],
  standalone: false,
})
export class TemplateGroupEditComponent implements OnInit {
  templateGroup: TemplateGroupDTO;
  templateGroupId: number;
  loading: boolean;
  form: UntypedFormGroup;
  variables: TemplateVariableDTO[];
  dropdown: string[];
  filledVariables: TemplateVariableDTO[];

  constructor(
    private activatedRoute: ActivatedRoute,
    private api: API,
    public errorHandler: ErrorService,
    public modal: ModalService,
    public util: UtilService,
    public navService: NavigationService,
    private destroyRef: DestroyRef
  ) {
    this.filledVariables = [];
    this.dropdown = Object.values(TemplateVariableType);
    const firstObj = new TemplateVariableDTO();
    firstObj.type = TemplateVariableType.TEXT;
    firstObj.name = '';
    this.variables = [firstObj];
    this.templateGroupId = null;
    this.loading = true;
    this.form = new UntypedFormGroup({
      Name: new UntypedFormControl('', [Validators.required]),
    });
  }

  get Name() {
    return this.form.get('Name');
  }

  ngOnInit(): void {
    this.activatedRoute.params.subscribe(params => {
      this.templateGroupId = params.templateGroupID;
      this.refreshFromServer();
    });
    this.registerFormChangesListener();
  }

  private registerFormChangesListener() {
    this.Name.valueChanges.subscribe(value => {
      this.templateGroup.name = value;
    });
  }

  updateForm() {
    this.Name.setValue(this.templateGroup.name);
    this.Name.markAsTouched();
  }

  nextStep(wizard, nextTab) {
    wizard.selectTab(nextTab);
  }

  private refreshFromServer() {
    this.loading = true;
    if (!this.templateGroupId) {
      this.templateGroup = new TemplateGroupDTO();
      this.loading = false;
    } else {
      this.api.mm_templating_provider_TemplateGroup_retrieveById(this.templateGroupId).subscribe({
        next: (templateGroup: TemplateGroupDTO) => {
          this.templateGroup = templateGroup;
          this.variables = templateGroup.variables;
          this.updateForm();
          this.Name.disable();
          this.loading = false;
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    }
  }

  updateTemplateGroup() {
    if (!this.templateGroup.id) {
      if (this.templateGroup.name && this.checkVariables() && this.checkAllUnique()) {
        this.loading = true;
        this.fillFilledVariables();
        this.templateGroup.variables = this.filledVariables;
        this.api
          .mm_templating_provider_TemplateGroup_create(this.templateGroup)
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            take(1),
            finalize(() => {
              this.loading = false;
            })
          )
          .subscribe({
            next: (createdTemplateGroup: TemplateGroupDTO) => {
              this.templateGroup = createdTemplateGroup;
              this.navService.navigateToPoint(RoutingID.templateGroups);
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      }
    }
  }

  addTemplateVariable() {
    const tempObj: TemplateVariableDTO = new TemplateVariableDTO();
    tempObj.name = '';
    tempObj.type = TemplateVariableType.TEXT;
    this.variables.push(tempObj);
  }

  checkVariables(): boolean {
    let res = false;
    for (const element of this.variables) {
      if (element.name.length > 0 && element.name.trim().length > 0) {
        res = true;
      }
    }
    return res;
  }

  fillFilledVariables() {
    this.filledVariables = [];
    for (const element of this.variables) {
      if (element.name.length > 0 && element.name.trim().length > 0) {
        element.name = element.name.trim();
        this.filledVariables.push(element);
      }
    }
  }

  deleteRow(index: number) {
    if (index > 0) {
      this.variables.splice(index, 1);
    }
  }

  getUniqueStatus(i: number, variable: TemplateVariableDTO) {
    for (const [index, tVariable] of this.variables.entries()) {
      if (index === i) {
        continue;
      }
      if (
        (tVariable.name === variable.name || tVariable.name.trim() === variable.name.trim()) &&
        tVariable.name.length > 0 &&
        tVariable.name.trim().length > 0
      ) {
        return false;
      }
    }
    return true;
  }

  checkAllUnique() {
    let res = true;
    for (const [index, tVariable] of this.variables.entries()) {
      // check for every variable if unique
      res = this.getUniqueStatus(index, tVariable);
      if (!res) {
        return res; // return in case of one false unique check
      }
    }
    return res;
  }
}
